import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useUnits } from '../../contexts/UnitsContext';
import './WeekPlanCard.css';

interface PlannedWorkout {
  id: number;
  planned_date: string;       // YYYY-MM-DD
  day: string;                // "Monday"
  workout_type: string;
  planned_distance: number | null;
  status: 'pending' | 'completed' | 'missed' | 'partial' | string;
}

const WeekPlanCard: React.FC = () => {
  const { formatDist } = useUnits();
  const [workouts, setWorkouts] = useState<PlannedWorkout[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getPlannedWorkouts()
      .then((resp: any) => {
        if (cancelled) return;
        setWorkouts(resp.workouts || []);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to load plan');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (error || !workouts) return null;

  // Today's date in user's local timezone — compare as YYYY-MM-DD strings
  // to avoid time-of-day drift across timezones.
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Filter: today + future days only; skip Rest (uninformative for "what's left").
  // Strength stays — it's a real commitment.
  const remaining = workouts
    .filter(w => w.planned_date >= todayStr)
    .filter(w => w.workout_type !== 'Rest');

  if (remaining.length === 0) {
    // Either the week is done or no plan exists for this week.
    const anyThisWeek = workouts.some(w => w.workout_type !== 'Rest');
    return (
      <div className="week-plan-card">
        <div className="week-plan-header">
          <h3>This Week's Plan</h3>
          <Link to="/training-plan" className="week-plan-link">View full plan →</Link>
        </div>
        <p className="week-plan-empty">
          {anyThisWeek
            ? "All this week's workouts are done — nice work."
            : "No plan generated for this week yet."}
        </p>
      </div>
    );
  }

  const statusIcon = (w: PlannedWorkout): React.ReactNode => {
    if (w.status === 'completed') return <span className="wp-icon wp-icon--done" title="Completed">✓</span>;
    if (w.status === 'missed' || w.status === 'partial') return <span className="wp-icon wp-icon--miss" title={w.status === 'partial' ? 'Partial' : 'Missed'}>✗</span>;
    return <span className="wp-icon wp-icon--pending" title="Pending">·</span>;
  };

  const dayShort = (iso: string, dayName: string): string => {
    // "2026-05-06" + "Wednesday" → "Wed 5/6"
    const [, m, d] = iso.split('-');
    return `${dayName.slice(0, 3)} ${parseInt(m, 10)}/${parseInt(d, 10)}`;
  };

  const isRunType = (t: string) =>
    t !== 'Strength Training' && t !== 'Rest';

  return (
    <div className="week-plan-card">
      <div className="week-plan-header">
        <h3>This Week's Plan</h3>
        <Link to="/training-plan" className="week-plan-link">View full plan →</Link>
      </div>
      <ul className="week-plan-list">
        {remaining.map(w => {
          const isToday = w.planned_date === todayStr;
          return (
            <li key={w.id} className={`week-plan-row ${isToday ? 'is-today' : ''}`}>
              <span className="wp-day">{dayShort(w.planned_date, w.day)}{isToday ? <span className="wp-today-tag"> · Today</span> : null}</span>
              <span className="wp-type">{w.workout_type}</span>
              <span className="wp-dist">
                {isRunType(w.workout_type) && w.planned_distance ? formatDist(w.planned_distance, 1) : ''}
              </span>
              {statusIcon(w)}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default WeekPlanCard;
